import { types, flow } from 'mobx-state-tree';
import moment from 'moment';
import {
  getMedia,
  getMediaById,
  patchMediaById,
  postMediaUpload,
  postMedia,
  deleteMediaById,
  updateExistingMediaById,
  DISALLOWED_PATCH_KEYS,
} from '../api/media';
import { handleResponse } from '../api';
import { getStore } from './index';

const MediaItem = types
  .model('MediaItem', {
    createdAt: types.optional(types.string, ''),
    updatedAt: types.optional(types.string, ''),
    state: types.optional(types.string, ''),
    copyright: types.optional(types.string, ''),
    title: types.optional(types.string, ''),
    originalFilename: types.optional(types.string, ''),
    owner: types.optional(types.string, ''),
    author: types.optional(types.string, ''),
    altText: types.optional(types.string, ''),
    tags: types.optional(types.array(types.string), []),
    class: types.optional(types.string, ''),
    source: types.optional(types.string, ''),
    sourceType: types.optional(types.string, ''),
    uuid: types.optional(types.string, ''),
    viewAccess: types.optional(types.array(types.string), []),
  })
  .views(self => ({
    get parsed() {
      return {
        ...self.toJSON(),
        createdAt: moment(self.createdAt).format('LL'),
        updatedAt: moment(self.updatedAt).format('LL'),
      };
    },
  }));

const parseMedia = mediadata => ({
  ...mediadata,
  // TODO: in some cases owner is returned null from the backend
  // maybe user uploaded something and the user was deleted?
  owner: mediadata['owner'] || '',
});

const MediaStore = types
  .model('MediaStore', {
    items: types.array(MediaItem),
    isLoading: types.optional(types.boolean, false),
  })
  .actions(self => {
    const addItem = data => {
      self.items.push(parseMedia(data));
    };

    const updateItem = data => {
      const parsed = parseMedia(data);
      const index = self.items.findIndex(i => i.uuid === parsed.uuid);
      self.items[index] = parsed;
    };

    const updateAttribute = (uuid, attribute, value) => {
      const index = self.items.findIndex(i => i.uuid === uuid);
      self.items[index][attribute] = value;
    };

    const loadAllMedia = flow(function*() {
      getStore('appStore').setIsLoading(true);
      self.isLoading = true;
      const response = yield getMedia();
      handleResponse([
        [
          200,
          () => {
            self.items = response.data.map(p =>
              MediaItem.create(parseMedia(p)),
            );
          },
        ],
        [
          'default',
          () => {
            console.log('error loading media all');
          },
        ],
      ])(response);
      getStore('appStore').setIsLoading(false);
      self.isLoading = false;
      return response;
    });

    const loadMediaItem = flow(function*(uuid) {
      const response = yield getMediaById(uuid);
      handleResponse([
        [
          200,
          () => {
            if (self.itemExists(response.data.uuid)) {
              updateItem(response.data);
            } else {
              addItem(response.data);
            }
          },
        ],
        [
          'default',
          () => {
            console.log('error loading media item', uuid);
          },
        ],
      ])(response);
      return response;
    });

    const submitChanges = flow(function*(uuid) {
      const item = self.getItem(uuid).toJSON();
      const itemData = Object.keys(item).reduce((acc, key) => {
        if (DISALLOWED_PATCH_KEYS.indexOf(key) > -1) return acc;
        acc[key] = item[key];
        return acc;
      }, {});
      const response = yield patchMediaById(uuid, itemData);
      handleResponse([
        [
          200,
          () => {
            getStore('notificationStore').raiseInfo(
              'notifications.patchMedia.success',
            );
          },
        ],
        [
          'default',
          () => {
            console.log('error patching media', uuid);
          },
        ],
      ])(response);
      return response;
    });

    const uploadFile = flow(function*(file) {
      const response = yield postMediaUpload(file);
      handleResponse([
        [
          200,
          () => {
            getStore('notificationStore').raiseInfo(
              'notifications.uploadFile.success',
            );
            addItem(response.data);
          },
        ],
        [
          'default',
          () => {
            console.log('error uploading file', file);
          },
        ],
      ])(response);
      return response;
    });

    const replaceFile = flow(function*(uuid, file) {
      const response = yield updateExistingMediaById(uuid, file);
      handleResponse([
        [
          200,
          () => {
            getStore('notificationStore').raiseInfo(
              'notifications.uploadFile.success',
            );
            updateItem(response.data);
          },
        ],
        [
          'default',
          () => {
            console.log('error uploading file', file);
          },
        ],
      ])(response);
      return response;
    });

    const createMedia = flow(function*(type, source, sourceType) {
      const data = {
        class: type,
        source,
        sourceType,
      };
      const response = yield postMedia(data);
      handleResponse([
        [
          200,
          () => {
            getStore('notificationStore').raiseInfo(
              'notifications.createMedia.success',
            );
            addItem(response.data);
          },
        ],
        [
          'default',
          () => {
            console.log('error creating media', data);
          },
        ],
      ])(response);
      return response;
    });

    const findItem = function(uuid) {
      if (self.itemExists(uuid)) return self.getItem(uuid);
      self.loadMediaItem(uuid);
      return null;
    };

    const deleteMediaItem = flow(function*(uuid) {
      const response = yield deleteMediaById(uuid);
      handleResponse([
        [
          200,
          () => {
            self.items = self.items.filter(i => i.uuid !== uuid);
          },
        ],
        [
          'default',
          () => {
            console.log('error deleting media item', uuid);
          },
        ],
      ])(response);
      return response;
    });

    return {
      uploadFile,
      replaceFile,
      createMedia,
      loadAllMedia,
      loadMediaItem,
      deleteMediaItem,
      updateAttribute,
      submitChanges,
      findItem,
    };
  })
  .views(self => ({
    getItem: uuid => {
      return self.items.find(u => u.uuid === uuid);
    },
    itemExists: uuid => {
      return self.items.findIndex(i => i.uuid === uuid) > -1;
    },
  }));

export { MediaStore };
